import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { EnumPaymentType } from "@/api/cashier";
import {
  getTransactionDetailsOptions,
  getWithdrawalRequestStatusOptions,
} from "@/api/cashier/@tanstack/react-query.gen";
import { ErrorPage } from "@/components/error-page/ErrorPage";
import { TransactionDetails } from "@/components/transaction/transaction-details/TransactionDetails";
import { TransactionHeader } from "@/components/transaction/transaction-header/TransactionHeader";
import { TransactionTimeline } from "@/components/transaction/transaction-timeline/TransactionTimeline";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useTradingAccounts } from "@/hooks/useTradingAccounts";
import { ECashierFlows, ERoutes } from "@/utils/enums.ts";
import { moneyToMajorUnits } from "@/utils/helpers";

export const TransactionHistoryDetails = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { type, id } = useParams();
  const { currencies } = useCurrencies();
  const { data: accounts, isLoading: isAccountsLoading } = useTradingAccounts();

  const validatedType: EnumPaymentType | undefined =
    type && Object.values(EnumPaymentType).includes(type as EnumPaymentType)
      ? (type as EnumPaymentType)
      : undefined;

  const navigateBackToHistory = useCallback(() => {
    navigate(`/${ECashierFlows.History}`);
  }, [navigate]);

  const {
    isError,
    isLoading,
    data: transaction,
    refetch: refetchTransactions,
  } = useQuery({
    ...getTransactionDetailsOptions({
      path: {
        paymentType: validatedType ?? EnumPaymentType.EnumPaymentTypeDeposit,
        referenceID: id ?? "",
      },
    }),
    enabled: Boolean(validatedType && id),
  });

  const { data: withdrawalStatus } = useQuery({
    ...getWithdrawalRequestStatusOptions({
      path: { withdrawalRequestID: transaction?.withdrawal_request_id ?? "" },
    }),
    enabled:
      validatedType === EnumPaymentType.EnumPaymentTypeWithdrawal &&
      Boolean(id) &&
      Boolean(transaction) &&
      Boolean(transaction?.withdrawal_request_id),
  });

  const transactionCurrencyPrimitive = useMemo(() => {
    if (transaction?.money.code) return currencies?.[transaction.money.code];
  }, [currencies, transaction?.money.code]);

  const processingCurrencyPrimitive = useMemo(() => {
    if (transaction?.money_in_processing_currency.code)
      return currencies?.[transaction.money_in_processing_currency.code];
  }, [currencies, transaction?.money_in_processing_currency.code]);

  const feeCurrencyPrimitive = useMemo(() => {
    if (transaction?.fee_money?.code)
      return currencies?.[transaction.fee_money.code];
  }, [currencies, transaction?.fee_money?.code]);

  const completableAccount = useMemo(() => {
    if (!transaction || !accounts) return undefined;
    return accounts.find((a) => a.id === transaction.from_trading_account);
  }, [accounts, transaction]);

  const pendingConfirmationStatus = useMemo(() => {
    if (
      !withdrawalStatus ||
      withdrawalStatus.confirmation_status !== "unapproved"
    )
      return undefined;
    const expiresAt =
      new Date(withdrawalStatus.created_at).getTime() +
      withdrawalStatus.time_limit_in_minutes * 60 * 1000;
    const initialCountdownSeconds = Math.max(
      0,
      Math.floor((expiresAt - Date.now()) / 1000)
    );
    if (initialCountdownSeconds === 0) return undefined;
    return {
      verificationType: withdrawalStatus.verification_type,
      initialCountdownSeconds,
    };
  }, [withdrawalStatus]);

  const handleCompleteNow = useCallback(() => {
    if (!transaction || !withdrawalStatus || !completableAccount) return;
    navigate(
      `/${ECashierFlows.Withdraw}/${ERoutes.Verification}/${withdrawalStatus.withdrawal_request_id}?verification=${withdrawalStatus.verification_type}`,
      {
        state: {
          withdrawal_request_id: withdrawalStatus.withdrawal_request_id,
          verification_type: withdrawalStatus.verification_type,
          created_at: new Date(withdrawalStatus.created_at).toISOString(),
          time_limit_in_minutes: withdrawalStatus.time_limit_in_minutes,
          selectedAccount: completableAccount,
          transactionData: { total_money: transaction.money },
        },
      }
    );
  }, [completableAccount, navigate, transaction, withdrawalStatus]);

  const errorPage = (
    <ErrorPage
      showButton={true}
      title={t("transactions.error.title")}
      message={t("transactions.error.text")}
      buttonLabel={t("transactions.error.button_text")}
      buttonAction={() => {
        validatedType && id ? refetchTransactions() : navigateBackToHistory();
      }}
    />
  );

  return isError || !validatedType
    ? errorPage
    : transaction && (
        <>
          <TransactionHeader
            transaction={transaction}
            tradingAccounts={accounts ?? []}
            clickEvent={navigateBackToHistory}
            isLoading={isLoading || isAccountsLoading}
          />
          <TransactionTimeline
            isLoading={isLoading}
            transactionType={validatedType}
            transactionTimeline={transaction.timeline ?? []}
            onCompleteNow={
              completableAccount && pendingConfirmationStatus
                ? handleCompleteNow
                : undefined
            }
            pendingConfirmationStatus={pendingConfirmationStatus}
          />
          {transactionCurrencyPrimitive && (
            <TransactionDetails
              isLoading={isLoading}
              amount={moneyToMajorUnits(transaction.money) ?? 0}
              feeAmount={moneyToMajorUnits(transaction.fee_money) ?? undefined}
              feeCurrency={feeCurrencyPrimitive}
              createdAt={transaction.created_at}
              transactionCurrency={transactionCurrencyPrimitive}
              transactionId={transaction.reference_id}
              exchangeRate={transaction.exchange_rate}
              exchangeRateUnit={transaction.exchange_rate_unit}
              transactionType={validatedType as EnumPaymentType}
              processingCurrency={processingCurrencyPrimitive}
              cashAdjustmentReason={transaction.cash_adjustment_reason}
              amountInProcessingCurrency={
                moneyToMajorUnits(transaction.money_in_processing_currency) ??
                undefined
              }
              netAmountInTradingCurrency={
                moneyToMajorUnits(transaction.net_money_in_trading_currency) ??
                undefined
              }
            />
          )}
        </>
      );
};
